import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import Chip from "@mui/joy/Chip";
import Alert from "@mui/joy/Alert";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    ScrollTable,
    WatchSignals,
} from "../components/AnalyticsShared";
import { useAsyncBlock } from "../hooks";
import { bidsRisksApi } from "../../bidsApi";
import { BidRiskInfo, BidRiskReason } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks, formatUtcLocalShort } from "../format";
import { Game } from "../../../../models/games";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

function riskLabel(reason: BidRiskReason): string {
    switch (reason) {
        case BidRiskReason.StuckActive:
            return "Зависла";
        case BidRiskReason.HighReserved:
            return "Застряла в резерве";
        case BidRiskReason.InsufficientBalance:
            return "Не хватает баланса";
        case BidRiskReason.BiddingDisabled:
            return "Заявки отключены у пользователя";
        default:
            return `Причина ${reason}`;
    }
}

function riskColor(reason: BidRiskReason): "danger" | "warning" | "neutral" {
    switch (reason) {
        case BidRiskReason.BiddingDisabled:
        case BidRiskReason.InsufficientBalance:
            return "danger";
        case BidRiskReason.HighReserved:
            return "warning";
        default:
            return "neutral";
    }
}

export function RisksTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;

    React.useEffect(() => {
        if (isActive && filters.take < 50) {
            onFiltersChange({ ...filters, take: 50 });
        }
        // только при входе на таб
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    const [state, refresh] = useAsyncBlock<BidRiskInfo[]>(
        () =>
            bidsRisksApi({
                take: filters.take || 50,
                stuckHours: filters.stuckHours,
                gameId: filters.gameId,
            }),
        isActive,
        [filters.take, filters.stuckHours, filters.gameId]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod={false}
                showTake
                showStuckHours
            />

            <AnalyticsCard
                title="Заявки, которые требуют внимания"
                subtitle="Очередь на ручной разбор саппортом или оператором."
                snapshotLabel="now"
                howToRead={
                    <List size="sm" marker="disc">
                        <ListItem>
                            <Typography level="body-sm">
                                Сначала отключённые и без баланса, потом резерв, потом зависшие
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                Параметр «старше N часов» — порог зависания
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                Даты с бэка в UTC, показаны в вашем локальном времени
                            </Typography>
                        </ListItem>
                    </List>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Число рисков в очереди",
                                growsIsGood: false,
                                ifGrows: "больше заявок требуют ручного разбора; не успеваете — повысьте приоритет саппорту.",
                                ifFalls: "операционка справляется, «хвост» разгребается.",
                            },
                            {
                                signal: "Число «Застряла в резерве»",
                                growsIsGood: false,
                                ifGrows: "покупки зависают в процессе дольше обычного; проверьте ордера/ботов.",
                                ifFalls: "резерв отрабатывает нормально.",
                            },
                            {
                                signal: "Число «Не хватает баланса»",
                                growsIsGood: false,
                                ifGrows: "пользователи ставят заявки без денег — мёртвый спрос раздувает стакан.",
                                ifFalls: "единичные случаи, не системная проблема.",
                            },
                            {
                                signal: "Число «Заявки отключены у пользователя»",
                                growsIsGood: false,
                                ifGrows: "открытые заявки остаются при выключенном режиме — нужно отменить или включить обратно.",
                                ifFalls: "настройки пользователей согласованы с открытыми заявками.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && (!state.data || state.data.length === 0) && (
                    <EmptyState hint="Сейчас нет рисковых заявок. Можно ослабить порог часов или сменить игру." />
                )}
                {!state.loading && !state.error && state.data && state.data.length > 0 && (
                    <>
                        <Alert color="warning" variant="soft" size="sm">
                            Найдено рисков: {formatInt(state.data.length)}
                        </Alert>
                        <ScrollTable>
                            <Table size="sm" stickyHeader sx={{ minWidth: 1100 }}>
                                <thead>
                                    <tr>
                                        <th>Причина</th>
                                        <th>Заявка</th>
                                        <th>Покупатель</th>
                                        <th>Скин</th>
                                        <th>Цена</th>
                                        <th>Кол-во</th>
                                        <th>Выкуплено</th>
                                        <th>Резерв</th>
                                        <th>Остаток</th>
                                        <th>Баланс</th>
                                        <th>Заявки вкл.</th>
                                        <th>Создана</th>
                                        <th>Обновлена</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {state.data.map((row) => (
                                        <tr key={row.bidId}>
                                            <td>
                                                <Chip
                                                    size="sm"
                                                    variant="solid"
                                                    color={riskColor(row.riskReason)}
                                                >
                                                    {riskLabel(row.riskReason)}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">#{row.bidId}</Typography>
                                            </td>
                                            <td>
                                                <Link
                                                    level="body-sm"
                                                    href={`/app/users/${String(row.userId)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ display: "block" }}
                                                >
                                                    {row.buyerName || "—"}
                                                </Link>
                                                <Link
                                                    level="body-xs"
                                                    href={`/app/bids?userId=${String(row.userId)}#list`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {String(row.userId)}
                                                </Link>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" sx={{ maxWidth: 160 }}>
                                                    {row.marketSkinId}
                                                </Typography>
                                            </td>
                                            <td>{formatMoneyKopecks(row.price)}</td>
                                            <td>{formatInt(row.count)}</td>
                                            <td>{formatInt(row.bought)}</td>
                                            <td>{formatInt(row.reserved)}</td>
                                            <td>{formatInt(row.remaining)}</td>
                                            <td>{formatMoneyKopecks(row.userRubles)}</td>
                                            <td>
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color={row.bidingEnabled ? "success" : "danger"}
                                                >
                                                    {row.bidingEnabled ? "Да" : "Нет"}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">
                                                    {formatUtcLocalShort(row.created)}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">
                                                    {formatUtcLocalShort(row.updated)}
                                                </Typography>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </ScrollTable>
                    </>
                )}
            </AnalyticsCard>
        </Box>
    );
}
